import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate, useLocation } from 'react-router-dom';
import { auditWithdraw } from '../api/modules/payment';
import { showMessage } from '../utils';
import { isInApp } from '../utils/environment';
import {
  WITHDRAW_STATUS,
  getStatusLabel,
  formatTime,
  formatAmount,
  generateReviewHistory
} from '../utils/withdrawUtils';

const WithdrawDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [withdrawData, setWithdrawData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    // 从路由状态获取提现数据
    if (location.state?.withdrawData) {
      setWithdrawData(location.state.withdrawData);
    } else {
      // 如果没有数据，尝试从URL参数获取ID
      const urlParams = new URLSearchParams(location.search);
      const withdrawId = urlParams.get('id');

      if (withdrawId) {
        // TODO: 这里可以添加根据ID获取详情的API调用
        // fetchWithdrawDetail(withdrawId);
        showMessage('Please access this page from the withdraw list', 'warning');
        navigate('/campaigns/invitations/withdraw-list');
      } else {
        // 没有任何数据，返回列表页
        navigate('/campaigns/invitations/withdraw-list');
      }
    }
  }, [location.state, location.search, navigate]);



  // 处理审核操作
  const handleAudit = async (status, reason = '') => {
    if (!withdrawData) return;

    setIsLoading(true);
    try {
      const params = {
        withdrawId: withdrawData.withdrawId,
        status: status,
        ...(reason && { auditReason: reason }),
        remark: reason || ''
      };

      auditWithdraw(params, (result) => {
        const { code, msg } = result;
        if (code === 200) {
          showMessage( 'operation successful', 'success');
          // 更新本地数据状态
          setWithdrawData(prev => ({
            ...prev,
            status: status,
            auditTime: new Date().toISOString(),
            ...(reason && { auditReason: reason })
          }));
          // 关闭对话框
          setRejectDialogOpen(false);
          setRejectReason('');
        } else {
          showMessage(msg || 'operation failed', 'error');
        }
      });
    } catch (error) {
      showMessage('network error', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // 处理拒绝请求
  const handleRejectRequest = () => {
    setRejectDialogOpen(true);
  };

  // 确认拒绝
  const handleConfirmReject = () => {
    if (!rejectReason.trim()) {
      showMessage('Please enter the reason for rejection', 'error');
      return;
    }
    handleAudit(WITHDRAW_STATUS.REJECTED, rejectReason.trim());
  };

  // 处理批准请求
  const handleApproveRequest = () => {
    handleAudit(WITHDRAW_STATUS.VERIFIED);
  };

  // 处理标记为已支付
  const handleMarkAsPaid = () => {
    handleAudit(WITHDRAW_STATUS.PAID);
  };



  if (!withdrawData) {
    return (
      <Container maxWidth="sm" sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
        <Box sx={{ pt: 2, pb: 4, px: 2, textAlign: 'center' }}>
          <Typography>Loading...</Typography>
        </Box>
      </Container>
    );
  }

  const reviewHistory = generateReviewHistory(withdrawData);

  return (
    <Container maxWidth="sm" sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
      <Box sx={{ pt: 2, pb: 4, px: 2 }}>
        {/* Header */}
        {!isInApp() && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 3,
            position: 'relative'
          }}>
            <IconButton
              sx={{
                position: 'absolute',
                left: -8,
                padding: '8px'
              }}
              onClick={() => navigate(-1)}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                fontSize: 17,
                fontWeight: 600,
                color: '#000',
                textAlign: 'center',
                width: '100%'
              }}
            >
              Detail
            </Typography>
          </Box>
        )}

        {/* Order Details */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ color: '#6B7280', fontSize: '16px' }}>
                Order ID
              </Typography>
              <Typography sx={{ color: '#000', fontSize: '16px', fontWeight: 600 }}>
                {withdrawData.withdrawId}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ color: '#6B7280', fontSize: '16px' }}>
                Amount
              </Typography>
              <Typography sx={{ color: '#000', fontSize: '16px', fontWeight: 600 }}>
                {formatAmount(withdrawData.price)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ color: '#6B7280', fontSize: '16px' }}>
                Status
              </Typography>
              <Typography sx={{ color: '#000', fontSize: '16px', fontWeight: 600 }}>
                {getStatusLabel(withdrawData.status)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ color: '#6B7280', fontSize: '16px' }}>
                Time
              </Typography>
              <Typography sx={{ color: '#000', fontSize: '16px', fontWeight: 600 }}>
                {formatTime(withdrawData.createTime)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Review History */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#000',
            mb: 3
          }}>
            Review History
          </Typography>

          {reviewHistory.map((item, index) => (
            <Box key={index} sx={{
              bgcolor: '#F9FAFB',
              borderRadius: '12px',
              p: 3,
              mb: index < reviewHistory.length - 1 ? 2 : 0
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>
                  {item.title}
                </Typography>
                <Typography sx={{ color: '#000', fontSize: '14px', fontWeight: 500 }}>
                  {item.time}
                </Typography>
              </Box>
              <Typography sx={{ color: '#000', fontSize: '14px', mb: item.handler ? 1 : 0 }}>
                {item.description}
              </Typography>
              {item.handler && (
                <Typography sx={{ color: '#6B7280', fontSize: '12px' }}>
                  {item.handler}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {withdrawData.status === WITHDRAW_STATUS.PENDING && (
            <>
              {/* 待审核状态：显示拒绝和批准按钮 */}
              <Button
                fullWidth
                variant="outlined"
                sx={{
                  py: 1.5,
                  fontSize: '16px',
                  fontWeight: 600,
                  borderRadius: '12px',
                  borderColor: '#E5E7EB',
                  color: '#6B7280',
                  backgroundColor: '#F9FAFB',
                  '&:hover': {
                    borderColor: '#D1D5DB',
                    backgroundColor: '#F3F4F6',
                  }
                }}
                onClick={handleRejectRequest}
                disabled={isLoading}
              >
                Reject Request
              </Button>

              <Button
                fullWidth
                variant="contained"
                sx={{
                  py: 1.5,
                  fontSize: '16px',
                  fontWeight: 600,
                  borderRadius: '12px',
                  backgroundColor: '#000',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#333',
                  }
                }}
                onClick={handleApproveRequest}
                disabled={isLoading}
              >
                Approve Request
              </Button>
            </>
          )}

          {withdrawData.status === WITHDRAW_STATUS.VERIFIED && (
            /* 已审核状态：显示标记为已支付按钮 */
            <Button
              fullWidth
              variant="contained"
              sx={{
                py: 1.5,
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '12px',
                backgroundColor: '#007AFF',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#0056CC',
                }
              }}
              onClick={handleMarkAsPaid}
              disabled={isLoading}
            >
              Mark as Paid
            </Button>
          )}
        </Box>

        {/* Reject Dialog */}
        <Dialog
          open={rejectDialogOpen}
          onClose={() => setRejectDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: '18px', fontWeight: 600 }}>
            Reject Request
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Please enter the reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={() => setRejectDialogOpen(false)}
              sx={{ color: '#6B7280' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmReject}
              variant="contained"
              sx={{
                backgroundColor: '#DC2626',
                '&:hover': { backgroundColor: '#B91C1C' }
              }}
              disabled={isLoading}
            >
              Confirm Reject
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default WithdrawDetail;